import { create } from "zustand";
import { webexStreamRegistry } from "../lib/webexStreamRegistry";
import logger from "../utils/logger";

/* ---------------- Types ---------------- */


interface WebexInitResponse {
  webexStatus: "SUCCESS" | "FAILED";
  errorMessage: unknown | null;
  webexResponse: any | null;
}

interface WebexMeetingStoreState {
  meeting: any | null;
  meetingStarted: boolean;

  localStream: MediaStream | null;
  remoteStreams: MediaStream[];
  lobbyMembers: any[];
  audioOnlyMembers: any[];

  cnr: string | null;
  courtRoom: string | null;

  initWebex: (
    accessToken: string,
    destination: string,
    stream: any,
  ) => Promise<WebexInitResponse>;

  leaveMeeting: () => Promise<void>;

  muteAudio: () => void;
  unmuteAudio: () => void;
  muteVideo: () => void;
  unmuteVideo: () => void;
}



const formatVideoStream = (stream: any) => ({
  userId: stream.__webexMemberId,
  name: stream.name || "Guest",
  isLocal: false,
  localMute: false,
  audioPaused: true,
  source: "webex",
  producerId: "video",
  videoProducerId: "video",
  audioProducerId: "audio",
  stream: stream,
  videoPaused: false,
  audioOnly: false,
});
 
const formatAudioOnlyMember = (member: any) => ({
  userId: member.id,
  name: member.name || "Guest",
  isLocal: false,
  localMute: false,
  audioPaused: member?.isAudioMuted,
  source: "webex",
  producerId: "audio",
  videoProducerId: null,
  audioProducerId: "audio",
  stream: null,
  videoPaused: true,
  audioOnly: true,
});

 const joinMeeting = async (meeting: any, w: any, stream: any,
    set: (state: Partial<WebexMeetingStoreState>) => void,
) => {
  logger.log("streammm..", stream);
      const microphone = await w.meetings.mediaHelpers.createMicrophoneStream();
      const camera = await w.meetings.mediaHelpers.createCameraStream();

      await meeting.joinWithMedia({
        joinOptions: { enableMultistream: true },
        mediaOptions: {
          localStreams: { microphone, camera },
          audioEnabled: true,
          videoEnabled: true,
          allowMediaInLobby: true,
          bundlePolicy: "max-bundle",
        },
      });

      webexStreamRegistry.local = camera.outputStream;

      set({
        localStream: camera.outputStream,
      });

      logger.log("✅ Webex meeting started...");
    };


     const bindEvents = (
  meeting: any,
  get: () => WebexMeetingStoreState,
  set: (state: Partial<WebexMeetingStoreState>) => void,
  lobbyMap: Map<string, any>,
  audioOnlyMap: Map<string, any>,
) => {
      meeting.on(
        "media:remoteVideo:layoutChanged",
        ({ activeSpeakerVideoPanes, memberVideoPanes }: any) => {
          const attach = (rm: any) => {
            rm.on("sourceUpdate", () => {
              if (rm.sourceState === "live" && rm.stream) {
                rm.stream.__webexMemberId = rm.memberId || rm.member?.id;
                webexStreamRegistry.remote.set(rm.stream.id, rm.stream);
              } else {
                webexStreamRegistry.remote.delete(rm.stream?.id);
              }

              // set({
              //   remoteStreams: [...webexStreamRegistry.remote.values(),...audioOnlyMap.values()],
              // });
            });
          };

          Object.values(activeSpeakerVideoPanes || {}).forEach((g: any) =>
            g.getRemoteMedia().forEach(attach),
          );

          Object.values(memberVideoPanes || {}).forEach(attach);
        },
      );

      meeting.members.on("members:update", (event: any) => {
        event.delta?.updated?.forEach((member: any) => {
          // 🔗 Attach CNR + CourtRoom to member (for filtering in UI)
          member.cnr = get().cnr;
          member.courtRoom = get().courtRoom;

          if (member.isInLobby) {
            lobbyMap.set(member.id, member);
          } else {
            lobbyMap.delete(member.id);
          }

          // Get current remote streams to check if member has video
          const currentRemoteStreams = [...webexStreamRegistry.remote.values()];
          const membersWithVideo = new Set(
            currentRemoteStreams
              .map((stream: any) => stream.__webexMemberId)
              .filter(Boolean)
          );

          // Only add to audioOnlyMembers if:
          // 1. Member is in meeting
          // 2. Member is not self
          // 3. Member is video muted (or has no video)
          // 4. Member does NOT have an active video stream
          const hasVideoStream = membersWithVideo.has(member.id);
          const shouldBeAudioOnly =
            member.isInMeeting &&
            !member.isSelf &&
            (member.isVideoMuted || !hasVideoStream) &&
            !hasVideoStream; // Explicitly check no video stream exists

          if (shouldBeAudioOnly) {
            audioOnlyMap.set(member.id, member);
          } else {
            // Remove from audioOnlyMembers if they have video or don't meet criteria
            audioOnlyMap.delete(member.id);
          }

          const videoStreams = Array.from(webexStreamRegistry.remote.values());

          const formattedVideoStreams = videoStreams.map(formatVideoStream)
          const formattedAudioOnly = Array.from(audioOnlyMap.values()).map(formatAudioOnlyMember)
           

          const mergedStreams: any = [
            ...formattedVideoStreams,
            ...formattedAudioOnly,
          ];

          set({
            lobbyMembers: [...lobbyMap.values()],
            remoteStreams: mergedStreams,
          });
        });
      });
    };

/* ---------------- Store ---------------- */

export const useWebexMeetingStore = create<WebexMeetingStoreState>(
  (set, get) => {
    let w: any = null;
    let m: any = null;

    const lobbyMap = new Map<string, any>();
    const audioOnlyMap = new Map<string, any>();

  
    return {
      meeting: null,
      meetingStarted: false,

      localStream: null,
      remoteStreams: [],
      lobbyMembers: [],
      audioOnlyMembers: [],

      cnr: null,
      courtRoom: null,

      initWebex: async (accessToken, destination, stream) => {
        try {
          if (!window.Webex) {
            logger.error("❌ Webex SDK not found");
            return {
              webexStatus: "FAILED",
              errorMessage: "Webex SDK not found",
              webexResponse: null,
            };
          }

          w = (window as any).Webex.init({
            credentials: { access_token: accessToken },
            config: { meetings: { enableMultistream: true } },
          });

          await new Promise((r) => setTimeout(r, 300));

          await w.meetings.register();

          // ✅ Save CNR + CourtRoom in store
          // set({
          //   cnr: meta.cnr,
          //   courtRoom: meta.courtRoom,
          // });

          m = await w.meetings.create(destination);
          m.config.enableMultistream = true;
            bindEvents(m, get, set, lobbyMap, audioOnlyMap);

          await joinMeeting(m, w, stream, set);

          set({
            meeting: m,
            meetingStarted: true,
          });

          return {
            webexStatus: "SUCCESS",
            errorMessage: null,
            webexResponse: m,
          };
        } catch (err) {
          logger.log("error in webex", err);
          return {
            webexStatus: "FAILED",
            errorMessage: err,
            webexResponse: null,
          };
        }
      },

      leaveMeeting: async () => {
        try {
          if (m) await m.leave();
          if (w) await w.meetings.unregister();
        } catch (e) {
          logger.error("err" , e);
        }

        webexStreamRegistry.remote.clear();
        webexStreamRegistry.local = null;
        lobbyMap.clear();
        audioOnlyMap.clear();

        set({
          meeting: null,
          meetingStarted: false,
          localStream: null,
          remoteStreams: [],
          lobbyMembers: [],
          audioOnlyMembers: [],
          cnr: null,
          courtRoom: null,
        });
      },

      muteAudio: () => {
        const meeting = get().meeting;
        if (meeting) meeting.muteAudio();
      },

      unmuteAudio: () => {
        const meeting = get().meeting;
        if (meeting) meeting.unmuteAudio();
      },

      muteVideo: () => {
        const meeting = get().meeting;
        if (meeting) meeting.muteVideo();
      },

      unmuteVideo: () => {
        const meeting = get().meeting;
        if (meeting) meeting.unmuteVideo();
      },
    };
  },
);
